/**
 * @cobuntu/widgets — app-origin navigation helper (vanilla).
 *
 * The widget bundles are served from the Cobuntu app host (e.g.
 * `https://community.belaescala.com/widgets/v1/auth.js`) and embedded two
 * different ways:
 *
 *   1. On a Cobuntu-PROXIED apex (`foodieslx.com`, `app.cobuntu.com`): the
 *      page and the widgets share an origin, and app routes (`/login`,
 *      `/marketplace/new`, …) are proxied same-origin. A relative path
 *      resolves correctly.
 *
 *   2. On a DECOUPLED landing (`belaescala.com` is a standalone marketing
 *      site; the app lives on `community.belaescala.com`): the widgets are
 *      loaded cross-origin from the app host, and the landing does NOT
 *      serve app routes. A relative `/login` resolves against the landing
 *      and 404s.
 *
 * `appNav()` turns an app-route path into an absolute URL on the origin
 * the bundle was served FROM (the app host), so top-level navigations
 * target the app in both cases. In case (1) that origin equals
 * `location.origin`, so behavior is byte-for-byte unchanged.
 *
 * SCOPE: full-page navigations only (`location.href` / anchor `href`).
 * NOT for `fetch()` — cross-origin authed fetches need the auth cookie,
 * which SameSite blocks on a decoupled landing; those keep using the
 * same-origin relative path + the apex proxy (see `api/authedFetch.ts`).
 */

/**
 * The origin the widget bundle was served from — captured EAGERLY at
 * module load. `document.currentScript` is only set while a classic
 * script runs its top-level body (it is null inside a later click
 * handler), so it must be read now, not lazily. Falls back to any loaded
 * widget `<script>` tag, then to `location.origin` (the same-origin /
 * proxied-apex case).
 */
const WIDGET_ORIGIN: string = (() => {
  try {
    if (typeof document === "undefined") {
      return typeof location !== "undefined" ? location.origin : "";
    }
    const current = document.currentScript as HTMLScriptElement | null;
    if (current?.src) return new URL(current.src).origin;
    // Any already-parsed widget bundle shares the same origin.
    const tag = document.querySelector<HTMLScriptElement>('script[src*="/widgets/"]');
    if (tag?.src) return new URL(tag.src).origin;
    return location.origin;
  } catch {
    return typeof location !== "undefined" ? location.origin : "";
  }
})();

/** The origin the widget bundle was served from (the Cobuntu app host). */
export function widgetOrigin(): string {
  return WIDGET_ORIGIN;
}

/**
 * Absolute URL to an app-route `path` on the app origin, for a TOP-LEVEL
 * navigation. Same-origin on a proxied apex; app-origin on a decoupled
 * landing (where a relative path would 404). A leading slash is
 * normalized in.
 */
export function appNav(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${WIDGET_ORIGIN}${normalized}`;
}
